import { formatPrice } from '../../../../modules/currency';
import type { ShippingChargeBreakdown } from '../../../../modules/api';
import { PaymentPromo } from './PaymentPromo';

export interface PaymentSectionProps {
  charge: ShippingChargeBreakdown;
  /** The delivery was canceled or refunded in full: a promotion that priced it
   *  is still named, but no longer claims to have saved anything. */
  refunded?: boolean;
}

/**
 * Real cost the player paid at order time (operator currency). game_platform
 * has no Stripe invoice and the chosen-option positions carry no price, so this
 * snapshot is the only place a cost shows. `shipping` already includes the
 * selected insurance/signature EasyPost add-on; when the backend also sends the
 * `insurance` split, that portion is carved out onto its own row. Legacy
 * charges (null/zero insurance) keep the single full-price Shipping row.
 */
export function PaymentSection({ charge, refunded = false }: PaymentSectionProps) {
  const insurance =
    charge.insurance != null && charge.insurance > 0 ? charge.insurance : null;
  const shipping =
    insurance !== null && charge.shipping != null
      ? Math.round((charge.shipping - insurance) * 100) / 100
      : charge.shipping;
  const rows: Array<{ label: string; value: number | null }> = [
    { label: 'Shipping', value: shipping },
    { label: 'Insurance', value: insurance },
    { label: 'Handling fee', value: charge.handling },
    // Only operators with sales tax enabled charge it; zero/null hides the row.
    { label: 'Sales tax', value: charge.salesTax },
  ];

  return (
    <section>
      <h3
        className="text-[17px] font-bold tracking-[-0.3px]"
        style={{ color: 'var(--co-MyPacksItemCard-titleColor, #FFFFFF)' }}
      >
        Payment
      </h3>

      <div className="mt-3 flex flex-col gap-2">
        {charge.promo && <PaymentPromo promo={charge.promo} refunded={refunded} />}
        {rows.map((r) =>
          r.value != null && r.value > 0 ? (
            <div key={r.label} className="flex items-center justify-between text-[14px]">
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>{r.label}</span>
              <span style={{ color: '#FFFFFF' }}>{formatPrice(r.value, 2)}</span>
            </div>
          ) : null,
        )}

        {charge.total != null && (
          <div
            className="mt-1 flex items-center justify-between border-t pt-2 text-[15px] font-bold"
            style={{ borderColor: 'rgba(255,255,255,0.12)' }}
          >
            <span style={{ color: '#FFFFFF' }}>Total paid</span>
            <span style={{ color: '#FFFFFF' }}>{formatPrice(charge.total, 2)}</span>
          </div>
        )}
      </div>

      <p className="mt-2 text-[12px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
        Shipping reflects your selected delivery option. Paid at order from your balance.
      </p>
    </section>
  );
}
